import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LessonFilters } from "../services/lessonService";
import { lessonService } from "../services/lessonService";
import type { MyLessonsResponse } from "../types/lesson";

export function useLessons(filters: LessonFilters = {}) {
  return useQuery({
    queryKey: ["lessons", filters],
    queryFn: () => lessonService.getLessons(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useLesson(id: string) {
  return useQuery({
    queryKey: ["lessons", id],
    queryFn: () => lessonService.getLesson(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useMyLessons(levelCode?: string) {
  return useQuery<MyLessonsResponse>({
    queryKey: ["lessons", "my-lessons", levelCode],
    queryFn: () => lessonService.getMyLessons(levelCode),
    staleTime: 1000 * 60 * 5,
  });
}

export function useScheduledClasses() {
  return useQuery({
    queryKey: ["scheduled-classes"],
    queryFn: () => lessonService.getScheduledClasses(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useUpcomingClasses() {
  return useQuery({
    queryKey: ["scheduled-classes", "upcoming"],
    queryFn: () => lessonService.getUpcomingClasses(),
    staleTime: 1000 * 60 * 2,
  });
}

export function useScheduledClass(id: string) {
  return useQuery({
    queryKey: ["scheduled-classes", id],
    queryFn: () => lessonService.getScheduledClass(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch schedule notifications for students
 */
export function useScheduleNotifications() {
  return useQuery({
    queryKey: ["schedule-notifications"],
    queryFn: () => lessonService.getScheduleNotifications(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to dismiss a schedule notification
 */
export function useDismissScheduleNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: lessonService.dismissScheduleNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-notifications"] });
    },
  });
}
